import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { esWeb } from '@renderer/lib/entorno';
import { useCarritoStore } from '@renderer/store/useCarritoStore';
import { esMomentoSeguro, leerPantalla } from '@renderer/lib/momentoSeguro';

/**
 * Sprint 116 — Que Claudia no se quede trabajando con la versión de la mañana.
 *
 * ## El agujero que cierra
 *
 * El servidor está bien: el `index.html` revalida en cada petición y cada compilación produce un
 * bundle con hash propio (`index-D6bOPK08.js`), así que **nadie que abra el sitio recibe algo
 * viejo**. El problema es el otro: quien **ya lo tiene abierto**.
 *
 * El POS se abre a las nueve de la mañana y se cierra a medianoche. Una aplicación de una sola
 * página no se entera de un despliegue — sigue ejecutando el JavaScript que descargó al abrirse.
 * Se puede desplegar un arreglo correctamente, verlo servido en el servidor, y que la operadora
 * siga usando el código de hace ocho horas. **Ningún ajuste de caché arregla eso**: hay que
 * avisarle.
 *
 * ## Cómo se entera
 *
 * Pidiendo el `index.html` cada pocos minutos y mirando a qué bundle apunta. Si el nombre cambió,
 * hubo despliegue. No hace falta endpoint nuevo, ni versión que alguien recuerde subir a mano, ni
 * *service worker*: el hash del bundle **ya es** la huella del build, y es la misma señal que
 * usamos para verificar los despliegues desde fuera.
 *
 * ## Lo que NO hace, y es lo importante
 *
 * **No recarga sola.** Si la página se recargara mientras está cobrando, se perdería el pedido a
 * medio capturar; el aviso espera, sin caducar, a que ella diga cuándo. Tampoco tapa nada ni
 * interrumpe: es una franja al pie, con una sola acción.
 *
 * **Sprint 166 — Ahora sí recarga sola, pero sólo cuando no hay nada que perder** (`momentoSeguro.ts`):
 * carrito vacío, ninguna ventana abierta, nada escrito a medias, no es la página de un cliente, y un
 * minuto sin tocar nada (o la pestaña escondida). Lo pidió José tras el 24/10: la dueña vendió toda la
 * tarde con la versión de las 12:42 porque recargar la mandaba a teclear el PIN (eso también se
 * arregló: `sesionGuardada.ts`). Y nunca dos veces por la misma versión: si tras recargar sigue
 * viéndose vieja (una caché en medio), se queda la franja y no se entra en un ciclo.
 */

/** Cada cuánto se pregunta. Cinco minutos: un despliegue no es una urgencia de segundos. */
const CADA_MS = 5 * 60_000;

/** El bundle que ESTA pestaña está ejecutando, leído del propio documento. */
function bundleActual(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
  return s?.src.match(/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;
}

async function bundleDelServidor(): Promise<string | null> {
  try {
    // `no-store` y un parámetro variable: se quiere el índice de verdad, no el que
    // guardó el navegador — que es justamente el que podría estar viejo.
    const r = await fetch(`/?v=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return null;
    return (await r.text()).match(/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;
  } catch {
    // Sin internet o servidor reiniciándose: no es noticia. Se reintenta luego.
    return null;
  }
}

const CLAVE_RECARGADO = 'pos-recargado-para';
const CLAVE_ULTIMA_RECARGA = 'pos-ultima-recarga-auto';

export function AvisoVersionNueva() {
  const [hayNueva, setHayNueva] = useState<string | null>(null);

  useEffect(() => {
    if (!hayNueva) return;
    if (sessionStorage.getItem(CLAVE_RECARGADO) === hayNueva) return; // ya se intentó con ésta
    // Auditoría `cyber` S166: si dos versiones se alternaran (caché o réplicas), no rebotar: una cada 30 min.
    const ultima = Number(sessionStorage.getItem(CLAVE_ULTIMA_RECARGA) ?? 0);
    if (Date.now() - ultima < 30 * 60_000) return;
    let ultimoToque = Date.now();
    const toque = () => {
      ultimoToque = Date.now();
    };
    const intentar = () => {
      const e = leerPantalla(useCarritoStore.getState().items.length, ultimoToque);
      if (!esMomentoSeguro(e)) return;
      sessionStorage.setItem(CLAVE_RECARGADO, hayNueva);
      sessionStorage.setItem(CLAVE_ULTIMA_RECARGA, String(Date.now()));
      window.location.reload();
    };
    window.addEventListener('pointerdown', toque, true);
    window.addEventListener('keydown', toque, true);
    document.addEventListener('visibilitychange', intentar);
    const t = setInterval(intentar, 15_000);
    return () => {
      window.removeEventListener('pointerdown', toque, true);
      window.removeEventListener('keydown', toque, true);
      document.removeEventListener('visibilitychange', intentar);
      clearInterval(t);
    };
  }, [hayNueva]);

  useEffect(() => {
    // En escritorio esto no aplica: ahí la versión la cambia el instalador.
    if (!esWeb()) return;
    const mio = bundleActual();
    if (!mio) return; // en desarrollo el script no lleva hash; no hay nada que comparar

    let vivo = true;
    const mirar = async () => {
      const suyo = await bundleDelServidor();
      if (vivo && suyo && suyo !== mio) setHayNueva(suyo);
    };

    /**
     * Sprint 151 — **Preguntar al llegar y al volver, no sólo cada cinco minutos.**
     *
     * El dueño lo cazó después de un despliegue: *"¿y por qué no sale el aviso de recargar?"*. El
     * mecanismo estaba bien —el servidor contesta el índice correcto— y fallaba **cuándo**
     * preguntaba. `setInterval` **sólo programa la siguiente**: la primera comprobación no ocurría
     * hasta pasados cinco minutos desde que se montó la pantalla.
     *
     * Y el hueco grande es el otro: los navegadores **congelan los temporizadores de una pestaña
     * que no está a la vista**. En una caja, el POS pasa horas detrás del navegador del banco, de
     * WhatsApp Web o de una ventana de Excel; ahí el reloj se detiene y el aviso no llega nunca
     * —que es exactamente el caso para el que se construyó: *"el POS se abre a las nueve y se
     * cierra a medianoche"*—.
     *
     * Por eso se pregunta también cuando la pestaña **vuelve a verse**: es el instante en que
     * alguien va a usar el sistema y el único momento en el que el aviso sirve de algo.
     */
    void mirar();
    const t = setInterval(mirar, CADA_MS);
    const alVolver = () => {
      if (document.visibilityState === 'visible') void mirar();
    };
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      vivo = false;
      clearInterval(t);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, []);

  if (!hayNueva) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t-4 border-emerald-600 bg-emerald-50 px-4 py-3 shadow-lg">
      <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-3 text-center">
        <div className="text-sm font-semibold text-emerald-900">
          El sistema se actualizó.{' '}
          <span className="font-normal">
            Se pondrá al día solo en cuanto termines lo que estás haciendo, o recarga tú ahora.
          </span>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 font-bold text-white shadow-sm hover:bg-emerald-700"
        >
          <RefreshCw className="h-4 w-4" />
          Recargar ahora
        </button>
      </div>
    </div>
  );
}
